import { MutableRefObject, useCallback, useMemo, useRef } from "react";
import { Dimensions } from "react-native";
import findIndex from "../helpers/findIndex";
import { KeyExtractor, OnEndReached, PositionLookupRef } from "../types";

const numActiveMain = 3;
const useGetActiveData = <TItem>({
	dataRef,
	keyExtractor,
	curKey,
	curIndex,
	carouselItemDimensions,
	onStartReached,
	startReachedIndexThreshold,
	onEndReached,
	endReachedIndexThreshold,
	positionLookup,
	processNewActiveItem,
}: {
	dataRef: MutableRefObject<TItem[]>;
	keyExtractor: KeyExtractor<TItem>;
	curKey: MutableRefObject<string>;
	curIndex: MutableRefObject<number>;
	carouselItemDimensions: { width: number; height: number };
	onStartReached: OnEndReached | undefined;
	startReachedIndexThreshold: number;
	onEndReached: OnEndReached | undefined;
	endReachedIndexThreshold: number;
	positionLookup: PositionLookupRef;
	processNewActiveItem: () => void;
}) => {
	const numActiveCarousel = useMemo(() => {
		return Math.ceil(1.4 * (Dimensions.get("window").width / carouselItemDimensions.width));
	}, [carouselItemDimensions]);

	const isFirstCall = useRef(true);
	const didCallOnStartReached = useRef(false);
	const didCallOnEndReached = useRef(false);
	const maybeCallOnStartOrEndReached = useCallback(
		(item: TItem) => {
			if (didCallOnStartReached.current && didCallOnEndReached.current) {
				return;
			}
			// There's some weird set state issues that happen since this is called during render
			if (isFirstCall.current) return;
			if (!onStartReached && !onEndReached) return;

			const key = keyExtractor({ item });
			const position = positionLookup.current[key];
			if (!position) return;
			const index = position.dataIndex;
			if (onStartReached && index <= startReachedIndexThreshold) {
				onStartReached({ index });
				didCallOnStartReached.current = true;
			}
			if (onEndReached && index >= dataRef.current.length - endReachedIndexThreshold) {
				onEndReached({ index });
				didCallOnEndReached.current = true;
			}
		},
		[
			onStartReached,
			onEndReached,
			keyExtractor,
			positionLookup,
			startReachedIndexThreshold,
			dataRef,
			endReachedIndexThreshold,
		],
	);

	/**
	 * Returns an object representing the amount of data to actually be rendered
	 *
	 * Subset of the full data array
	 */
	const getActiveData = useCallback(() => {
		const retMain: { item: TItem; index: number }[] = [];
		const retCarousel: { item: TItem; index: number }[] = [];

		didCallOnStartReached.current = false;
		didCallOnEndReached.current = false;
		const center = findIndex({
			data: dataRef.current,
			keyExtractor,
			targetKey: curKey.current,
			prevIndex: curIndex.current,
		});
		curIndex.current = center;
		// Need to call after setting new index
		processNewActiveItem();

		const halfActiveMain = Math.ceil(numActiveMain / 2);
		for (let i = center + halfActiveMain; i >= center - halfActiveMain; i--) {
			const cur = dataRef.current[i];
			if (cur) {
				maybeCallOnStartOrEndReached(cur);
				retMain.push({
					item: cur,
					index: i,
				});
			}
		}

		const halfActiveCarousel = Math.ceil(numActiveCarousel / 2);
		for (let i = center + halfActiveCarousel; i >= center - halfActiveCarousel; i--) {
			const cur = dataRef.current[i];
			if (cur) {
				maybeCallOnStartOrEndReached(cur);
				retCarousel.push({
					item: cur,
					index: i,
				});
			}
		}

		isFirstCall.current = false;
		return {
			main: retMain,
			carousel: retCarousel,
		};
	}, [
		numActiveCarousel,
		keyExtractor,
		maybeCallOnStartOrEndReached,
		processNewActiveItem,
		curKey,
		curIndex,
		dataRef,
	]);
	return getActiveData;
};

export default useGetActiveData;
